import type { AffiliationType } from "@/api/affiliationType";
import type { AccountRole } from "@/pages/login/type/loginType";

/**
 * 소속 분류(affiliation_type)별 페이지 접근 정책 단일 출처.
 *
 * 라우터 가드(직접 URL 접근 차단)와 사이드바 메뉴 노출이 모두 이 정책을 공유한다.
 * 페이지 접근 권한을 바꾸려면 이 표만 수정하면 되고, 라우터/사이드바는 자동으로 따라간다.
 *
 * - "*"            : 모든 페이지 접근 허용
 * - { except: [] } : 나열된 경로만 제외하고 전부 허용 (제외 목록, 새 페이지 자동 포함)
 * - string[]       : 나열된 라우트 경로만 접근 허용 (화이트리스트)
 *
 * 경로는 라우트 path 기준(선행 "/"). 새 페이지를 특정 유형에 노출하려면
 * 해당 유형 목록에 경로를 추가한다.
 */
type PageAccessPolicy = "*" | { except: readonly string[] } | readonly string[];

export const PAGE_ACCESS_BY_TYPE: Record<AffiliationType, PageAccessPolicy> = {
  // 플랫폼 운영: 전체 페이지 접근
  MASTER: "*",
  /**
   * 운영사: 로밍 관리 / 운영사 관리 / 고객사 전용 화면 제외하고 전체 접근.
   *
   * `except` 는 새 라우트를 자동 허용하므로 **고객사 전용 화면을 추가할 때마다
   * 여기에도 넣어야 한다** — 빠뜨리면 운영사에게 그대로 노출된다.
   */
  CPO: {
    except: ["/roaming", "/operator", "/usage-history", "/usage-summary"],
  },
  /**
   * 고객사: 대시보드 / 이용 내역 / 이용 집계 / 충전카드 관리 / 계정 관리.
   *
   * 충전 내역·매출 집계는 운영사 관점 화면이라 접근 대상이 아니다 — 고객사는
   * 자기 세션만 보는 이용 내역·이용 집계를 쓴다.
   *
   * 충전기 관리는 항목 단위 미노출(운영 정보·모뎀·통신망·메모, 등록·수정·삭제·원격제어,
   * 적용 요금제)을 갖춘 뒤 열었다. CS 관리는 아직 대응 이력·연결 티켓 미노출이 없어 닫혀 있다.
   */
  CLIENT: [
    "/dashboard",
    "/usage-history",
    "/usage-summary",
    "/charger",
    "/charger-card",
    "/account",
  ],
};

/** 로그인 직후 진입 경로. 모든 유형이 접근 가능해야 한다 */
export const HOME_PATH = "/dashboard";

/** 접근 거부 시 이동시킬 기본 경로 */
export const ACCESS_FALLBACK_PATH = HOME_PATH;

/**
 * 해당 소속 분류가 주어진 경로에 접근 가능한지 판정.
 *
 * affiliation_type 미확정(null, my-info 로딩 전 등)이면 막지 않는다.
 * 하위 경로(`/station/123` 등)도 상위 정책을 따른다.
 */
export function canAccessPage(
  affiliationType: AffiliationType | null,
  path: string,
): boolean {
  if (affiliationType == null) return true;
  const policy = PAGE_ACCESS_BY_TYPE[affiliationType];
  if (policy === "*") return true;
  const matches = (p: string) => path === p || path.startsWith(`${p}/`);
  if ("except" in policy) return !policy.except.some(matches);
  return policy.some(matches);
}

/**
 * 소속 분류별 **조회 항목** 노출 정책 단일 출처.
 *
 * 페이지 단위 접근은 `PAGE_ACCESS_BY_TYPE`, 쓰기는 `MEMBER_WRITABLE_FEATURES` 가 맡는다.
 * 이 표는 "들어갈 수는 있는 화면 안에서 무엇이 보이는가" 만 담당한다 —
 * 나열된 소속 분류에게만 해당 항목이 노출된다.
 *
 * 넣을 것: 항목·섹션·목록 열의 노출 여부.
 * 넣지 말 것: 같은 것을 다르게 부르는 라벨(`충전 내역`/`이용 내역`), 이동 경로,
 * 쓰기 액션(그쪽은 `canWrite` 축이다). 이 구분이 무너지면 표가 감사 가치를 잃는다.
 */
export const VIEW_ACCESS_BY_FEATURE = {
  /** 충전기 상세 — 운영 정보 섹션(설치일·개통일 등) */
  chargerOperationInfo: ["MASTER", "CPO"],
  /** 충전기 상세 — 모뎀·통신망 정보 섹션 */
  chargerModem: ["MASTER", "CPO"],
  /** 충전기 상세 — 고장 내역 조회 */
  chargerFaultHistory: ["MASTER", "CPO"],
  /** 충전기 상세 — 통신 로그 조회 */
  chargerCommLog: ["MASTER", "CPO"],
  /** 충전기 상세 — 원격 제어 (권한은 별도로 canWriteFeature("chargerControl") 가 본다) */
  chargerRemoteControl: ["MASTER", "CPO"],
  /** 적용 요금제 (목록 열·상세) */
  chargerPricePolicy: ["MASTER", "CPO"],
  /** 소유 운영사 (목록 열·상세 행). 운영사에게는 전부 자기 이름이라 뺀다 */
  ownerCpo: ["MASTER", "CLIENT"],
  /** 목록의 소속 열. 비-MASTER 는 자사 것만 조회돼 전부 같은 값이다 */
  affiliationColumn: ["MASTER"],
  /** 충전 내역·이용 내역의 충전소 운영사 열. 운영사는 전부 자기 이름, 고객사에게는 노출하지 않는다 */
  sessionStationCpo: ["MASTER"],
} as const satisfies Record<string, readonly AffiliationType[]>;

export type ViewFeature = keyof typeof VIEW_ACCESS_BY_FEATURE;

/**
 * 해당 소속 분류에게 그 항목을 보여줄지 판정.
 *
 * 소속 미확정(null)이면 **감춘다** — 페이지 접근과 반대이고 쓰기 판정과 같은 방침이다.
 * my-info 를 못 받은 퇴화 상태에서 운영사 전용 항목의 값이 스치듯 노출되는 쪽이
 * 섹션이 늦게 뜨는 쪽보다 나쁘다.
 */
export function canViewFeature(
  affiliationType: AffiliationType | null,
  feature: ViewFeature,
): boolean {
  if (affiliationType == null) return false;
  // as const 로 좁혀진 튜플이라 includes 가 인자를 거부한다 — 조회 시점에만 넓힌다
  const allowed: readonly AffiliationType[] = VIEW_ACCESS_BY_FEATURE[feature];
  return allowed.includes(affiliationType);
}

/**
 * 직책(role)별 쓰기 권한 정책 단일 출처.
 *
 * 기본값은 관리자(ADMIN) 전용이고, 아래 기능만 일반(MEMBER)도 쓰기가 허용된다 —
 * 현장 대응이 업무라 직책으로 막으면 일이 돌아가지 않는 영역이다.
 */
export const MEMBER_WRITABLE_FEATURES = [
  "csInquiry",
  "maintenanceTicket",
  "chargerControl",
] as const;

export type WriteFeature = (typeof MEMBER_WRITABLE_FEATURES)[number];

/**
 * 주어진 직책이 해당 기능을 쓸 수 있는지. feature 미지정이면 관리자 전용.
 *
 * 직책 미확정(null)이면 막는다 — 페이지 접근과 달리 쓰기는 잘못 열리면 되돌릴 수 없다.
 */
export function canWriteFeature(
  role: AccountRole | null,
  feature?: WriteFeature,
): boolean {
  if (role === "ADMIN") return true;
  if (role !== "MEMBER") return false;
  return feature != null && MEMBER_WRITABLE_FEATURES.includes(feature);
}
